import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, ClipboardCheck, Pencil, Plus, Trash2 } from 'lucide-react'
import { AppShell } from '~/components/layout/app-shell'
import { EmptyState } from '~/components/shared/empty-state'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Input } from '~/components/ui/input'
import { Badge } from '~/components/ui/badge'
import { CustomSelect } from '~/components/ui/custom-select'
import { useAsyncResource } from '~/hooks/use-async'
import {
    ClassificationBadge,
    PassLikelihoodBadge,
    StatusBadgeAssessment,
} from '~/components/assessment/assessment-badges'
import { scaleLabel } from '~/components/assessment/labels'
import { NewAssessmentForm } from '~/components/assessment/new-assessment-form'
import {
    createAssessment,
    deleteAssessment,
    listAssessments,
    updateAssessment,
} from '~/api/assessment'
import {
    getActiveBusinessContext,
    upsertActiveBusinessContext,
} from '~/api/business-context'
import { ApiError } from '~/api/client'
import type {
    AssessmentResponse,
    AssessmentStatus,
    BusinessContext,
    CreateAssessmentRequest,
} from '~/api/types'
import { formatDateTime } from '~/lib/date-utils'

type SortKey = 'recent' | 'created' | 'title'

const STATUS_FILTERS: { value: AssessmentStatus | 'all'; label: string }[] = [
    { value: 'all', label: '전체' },
    { value: 'collecting', label: '작성 중' },
    { value: 'scored', label: '채점 완료' },
    { value: 'reported', label: '보고서 완료' },
]

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
    { value: 'recent', label: '최근 진행순' },
    { value: 'created', label: '시작순' },
    { value: 'title', label: '제목순' },
]

/** 나의 HACCP 인증 AI — 저장된 평가 목록. */
export function CertificationListPage() {
    const navigate = useNavigate()
    const {
        data: loaded,
        loading,
        error: loadError,
        reload,
    } = useAsyncResource(listAssessments, [], '평가 목록을 불러오지 못했어요.')
    // 로딩 중에는 null(불러오는 중), 끝나면 데이터 또는 [](실패해도 빈 목록 표시).
    const items: AssessmentResponse[] | null = loading ? null : (loaded ?? [])
    const [query, setQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<AssessmentStatus | 'all'>(
        'all'
    )
    const [sortKey, setSortKey] = useState<SortKey>('recent')
    const [createError, setCreateError] = useState<string | null>(null)
    const error = createError ?? loadError
    const [showForm, setShowForm] = useState(false)
    const [creating, setCreating] = useState(false)
    const [activeProfile, setActiveProfile] = useState<BusinessContext | null>(
        null
    )

    useEffect(() => {
        let cancelled = false
        getActiveBusinessContext()
            .then((profile) => {
                if (!cancelled) setActiveProfile(profile)
            })
            .catch(() => {
                if (!cancelled) setActiveProfile(null)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const handleCreate = async (value: CreateAssessmentRequest) => {
        setCreating(true)
        setCreateError(null)
        try {
            const created = await createAssessment(value)
            navigate(`/certification/${created.id}`)
        } catch (err: unknown) {
            setCreateError(
                err instanceof ApiError
                    ? err.message
                    : '평가를 생성하지 못했어요.'
            )
            setCreating(false)
        }
    }

    const handleRename = async (a: AssessmentResponse) => {
        const next = window.prompt('평가 제목을 수정하세요.', a.title)
        if (next === null) return // 취소
        const trimmed = next.trim()
        if (!trimmed || trimmed === a.title) return
        setCreateError(null)
        try {
            await updateAssessment(a.id, { title: trimmed })
            reload()
        } catch (err: unknown) {
            setCreateError(
                err instanceof ApiError
                    ? err.message
                    : '제목을 수정하지 못했어요.'
            )
        }
    }

    const handleDelete = async (a: AssessmentResponse) => {
        const ok = window.confirm(
            `"${a.title}" 평가를 삭제할까요?\n답변, 채점 결과, 보고서도 함께 삭제됩니다.`
        )
        if (!ok) return
        setCreateError(null)
        try {
            await deleteAssessment(a.id)
            reload()
        } catch (err: unknown) {
            setCreateError(
                err instanceof ApiError
                    ? err.message
                    : '평가를 삭제하지 못했어요.'
            )
        }
    }

    // 검색어·상태 필터 + 정렬(클라이언트). 평가가 많아져도 빠르게 찾는다.
    const q = query.trim().toLowerCase()
    const isFiltering = q !== '' || statusFilter !== 'all'
    const filtered = items
        ? items
              .filter((a) => {
                  if (statusFilter !== 'all' && a.status !== statusFilter)
                      return false
                  if (!q) return true
                  return [
                      a.title,
                      a.industry ?? '',
                      a.classification ?? '',
                      scaleLabel(a.scale),
                  ].some((field) => field.toLowerCase().includes(q))
              })
              .sort((a, b) => {
                  if (sortKey === 'title')
                      return a.title.localeCompare(b.title, 'ko')
                  const key = sortKey === 'created' ? 'createdAt' : 'updatedAt'
                  return (b[key] ?? '').localeCompare(a[key] ?? '')
              })
        : items

    return (
        <AppShell>
            <div className='h-full overflow-hidden'>
                <div className='mx-auto flex h-full w-full max-w-[1680px] flex-col px-4 py-6 lg:px-8 2xl:px-10'>
                    <div className='mb-4 flex shrink-0 items-center justify-between gap-4'>
                        <h1 className='text-foreground text-lg font-semibold'>
                            HACCP 모의 평가 AI
                        </h1>
                    </div>

                    {error ? (
                        <ErrorBanner className='mb-3 shrink-0'>
                            {error}
                        </ErrorBanner>
                    ) : null}

                    <div className='grid min-h-0 flex-1 grid-cols-[360px_minmax(0,1fr)] gap-5 max-lg:grid-cols-1'>
                        <aside className='min-h-0 overflow-y-auto pr-1'>
                            {showForm ? (
                                <NewAssessmentForm
                                    onCreate={handleCreate}
                                    onSaveProfile={async (value) => {
                                        const saved =
                                            await upsertActiveBusinessContext(
                                                value
                                            )
                                        setActiveProfile(saved)
                                        return saved
                                    }}
                                    activeProfile={activeProfile}
                                    onCancel={() => setShowForm(false)}
                                    creating={creating}
                                />
                            ) : (
                                <section className='border-border bg-card rounded-md border p-5'>
                                    <div className='bg-accent text-primary mb-4 grid size-11 place-items-center rounded-md'>
                                        <ClipboardCheck className='size-5' />
                                    </div>
                                    <h2 className='text-foreground text-base font-semibold'>
                                        평가를 시작하세요
                                    </h2>
                                    <p className='text-muted-foreground mt-2 text-sm leading-6'>
                                        AI가 업종을 확인하고 체크리스트 답변을
                                        채운 뒤, 채점과 보고서 생성까지
                                        이어갑니다.
                                    </p>
                                    <Button
                                        className='mt-5 w-full'
                                        onClick={() => setShowForm(true)}>
                                        <Plus />새 평가
                                    </Button>
                                </section>
                            )}
                        </aside>

                        <section className='border-border bg-background flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border'>
                            <div className='border-border flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3'>
                                <div>
                                    <h2 className='text-foreground text-sm font-semibold'>
                                        평가 목록
                                    </h2>
                                    <p className='text-muted-foreground mt-0.5 text-xs'>
                                        진행 중이거나 완료된 인증 평가를 이어서
                                        확인합니다.
                                    </p>
                                </div>
                                <div className='flex shrink-0 items-center gap-2'>
                                    {items && items.length > 0 ? (
                                        <Input
                                            type='search'
                                            value={query}
                                            onChange={(e) =>
                                                setQuery(e.target.value)
                                            }
                                            placeholder='제목·업종·분류 검색'
                                            aria-label='평가 검색'
                                            className='h-8 w-56 text-sm'
                                        />
                                    ) : null}
                                    <span className='text-muted-foreground shrink-0 text-xs'>
                                        {items
                                            ? isFiltering
                                                ? `${filtered?.length ?? 0}/${items.length}건`
                                                : `${items.length}건`
                                            : '불러오는 중'}
                                    </span>
                                </div>
                            </div>

                            {items && items.length > 0 ? (
                                <div className='border-border flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-4 py-2'>
                                    <div
                                        className='flex flex-wrap items-center gap-1.5'
                                        role='group'
                                        aria-label='상태 필터'>
                                        {STATUS_FILTERS.map((s) => (
                                            <button
                                                key={s.value}
                                                type='button'
                                                aria-pressed={
                                                    statusFilter === s.value
                                                }
                                                onClick={() =>
                                                    setStatusFilter(s.value)
                                                }
                                                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                                                    statusFilter === s.value
                                                        ? 'border-primary bg-primary text-primary-foreground'
                                                        : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
                                                }`}>
                                                {s.label}
                                            </button>
                                        ))}
                                    </div>
                                    <label className='text-muted-foreground flex shrink-0 items-center gap-1.5 text-xs'>
                                        정렬
                                        <CustomSelect
                                            value={sortKey}
                                            onValueChange={(v) =>
                                                setSortKey(v as SortKey)
                                            }
                                            options={SORT_OPTIONS}
                                            ariaLabel='정렬 기준'
                                            containerClassName='w-32'
                                            className='h-8 px-2 text-xs'
                                        />
                                    </label>
                                </div>
                            ) : null}

                            <div className='min-h-0 flex-1 overflow-y-auto p-4'>
                                {items === null ? (
                                    <p className='text-muted-foreground text-sm'>
                                        평가 목록을 불러오는 중…
                                    </p>
                                ) : items.length === 0 ? (
                                    <EmptyState
                                        title='아직 저장된 평가가 없어요'
                                        description='새 평가를 시작해 체크리스트를 작성하고 채점·보고서를 받아보세요.'
                                    />
                                ) : filtered && filtered.length === 0 ? (
                                    <EmptyState
                                        title='조건에 맞는 평가가 없어요'
                                        description='검색어나 상태 필터를 바꿔 보세요.'
                                    />
                                ) : (
                                    <ul className='grid gap-2 xl:grid-cols-2'>
                                        {(filtered ?? []).map((a) => (
                                            <li
                                                key={a.id}
                                                className='group/card relative'>
                                                <div className='absolute right-2 bottom-2 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover/card:opacity-100 focus-within:opacity-100'>
                                                    <button
                                                        type='button'
                                                        onClick={() =>
                                                            handleRename(a)
                                                        }
                                                        aria-label={`${a.title} 제목 수정`}
                                                        title='제목 수정'
                                                        className='text-muted-foreground hover:bg-accent hover:text-primary focus-visible:ring-ring grid size-7 place-items-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-none'>
                                                        <Pencil className='size-4' />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        onClick={() =>
                                                            handleDelete(a)
                                                        }
                                                        aria-label={`${a.title} 평가 삭제`}
                                                        title='평가 삭제'
                                                        className='text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:ring-ring grid size-7 place-items-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-none'>
                                                        <Trash2 className='size-4' />
                                                    </button>
                                                </div>
                                                <Link
                                                    to={`/certification/${a.id}`}
                                                    className='border-border bg-card hover:border-primary group flex h-full items-start justify-between gap-3 rounded-md border p-4 transition-colors'>
                                                    <div className='min-w-0'>
                                                        <p className='text-foreground truncate text-sm font-semibold'>
                                                            {a.title}
                                                        </p>
                                                        <p className='text-muted-foreground mt-1 text-xs'>
                                                            {scaleLabel(
                                                                a.scale
                                                            )}
                                                            {a.industry
                                                                ? ` · ${a.industry}`
                                                                : ''}
                                                            {a.totalScore !=
                                                                null &&
                                                            a.maxScore != null
                                                                ? ` · 점수 ${a.totalScore}/${a.maxScore}`
                                                                : ''}
                                                            {a.grade
                                                                ? ` · 등급 ${a.grade}`
                                                                : ''}
                                                        </p>
                                                        <p className='text-muted-foreground mt-2 text-xs'>
                                                            시작{' '}
                                                            {formatDateTime(
                                                                a.createdAt
                                                            )}{' '}
                                                            · 최근 진행{' '}
                                                            {formatDateTime(
                                                                a.updatedAt
                                                            )}
                                                        </p>
                                                        <div className='mt-3 flex flex-wrap gap-1.5'>
                                                            <ClassificationBadge
                                                                classification={
                                                                    a.classification
                                                                }
                                                            />
                                                            {a.assessmentType ? (
                                                                <Badge tone='neutral'>
                                                                    {
                                                                        a.assessmentType
                                                                    }
                                                                </Badge>
                                                            ) : null}
                                                            <StatusBadgeAssessment
                                                                status={
                                                                    a.status
                                                                }
                                                            />
                                                            {a.passLikelihood ? (
                                                                <PassLikelihoodBadge
                                                                    likelihood={
                                                                        a.passLikelihood
                                                                    }
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <ArrowRight className='text-muted-foreground group-hover:text-primary mt-0.5 size-4 shrink-0' />
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </AppShell>
    )
}
